import React, { useState, useEffect } from 'react';
import ConfirmationDialog from '../ConfirmationDialog';
import '../../styles.css';

function CompensationEditor({ param, groupIdx, paramIdx, setTabs, isNew = false, currentTab, allParams }) {
  const [showDialog, setShowDialog] = useState(false);
  const [compensation, setCompensation] = useState(isNew ? {
    parameter: '',
    adjustedBy: '',
    ratios: [],
    compensations: []
  } : { ...param.value, title: param.name });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showParameterDropdown, setShowParameterDropdown] = useState(false);
  const [showAdjustedByDropdown, setShowAdjustedByDropdown] = useState(false);
  const [parameterSuggestions, setParameterSuggestions] = useState([]);
  const [adjustedBySuggestions, setAdjustedBySuggestions] = useState([]);
  const [compensationDropdowns, setCompensationDropdowns] = useState({});
  const [compensationSuggestions, setCompensationSuggestions] = useState({});

  useEffect(() => {
    if (!isNew) {
      setCompensation({ ...param.value, title: param.name });
    }
  }, [param, isNew]);

  const generateTitle = (parameter) => {
    return `${parameter} Compensation`;
  };

  const updateParameterSuggestions = (inputValue) => {
    if (!inputValue.trim()) {
      setParameterSuggestions([]);
      setShowParameterDropdown(false);
      return;
    }
    const filtered = allParams
      .filter(param => param.name.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, 5);
    setParameterSuggestions(filtered);
    setShowParameterDropdown(filtered.length > 0);
  };

  const updateAdjustedBySuggestions = (inputValue) => {
    if (!inputValue.trim()) {
      setAdjustedBySuggestions([]);
      setShowAdjustedByDropdown(false);
      return;
    }
    const filtered = allParams
      .filter(param => param.name.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, 5);
    setAdjustedBySuggestions(filtered);
    setShowAdjustedByDropdown(filtered.length > 0);
  };

  const updateCompensationSuggestions = (index, inputValue) => {
    if (!inputValue.trim()) {
      setCompensationSuggestions(prev => ({ ...prev, [index]: [] }));
      setCompensationDropdowns(prev => ({ ...prev, [index]: false }));
      return;
    }
    const filtered = allParams
      .filter(param => param.name.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, 5);
    setCompensationSuggestions(prev => ({ ...prev, [index]: filtered }));
    setCompensationDropdowns(prev => ({ ...prev, [index]: filtered.length > 0 }));
  };

  const addCompensation = () => {
    if (!compensation.parameter || !compensation.adjustedBy || compensation.ratios.length === 0) {
      alert('All fields are required');
      return;
    }

    const title = generateTitle(compensation.parameter);

    // Check for duplicate compensation
    const existingCompensation = allParams.find(p => p.name === title);
    if (existingCompensation) {
      alert(`A compensation for ${compensation.parameter} already exists.`);
      return;
    }
    const compToAdd = { ...compensation, title };
    setTabs(prevTabs => {
      const updatedTabs = [...prevTabs];
      const targetGroupIdx = updatedTabs[currentTab].groups.length - 1;
      const group = updatedTabs[currentTab].groups[targetGroupIdx];
      group.params.push({
        name: title,
        value: {
          parameter: compToAdd.parameter,
          adjustedBy: compToAdd.adjustedBy,
          ratios: compToAdd.ratios,
          compensations: compToAdd.compensations
        }
      });
      setShowDialog(false);
      setCompensation({
        parameter: '',
        adjustedBy: '',
        ratios: [],
        compensations: []
      });
      return updatedTabs;
    });
  };

  const updateCompensation = () => {
    const title = generateTitle(compensation.parameter);

    // Check for duplicate compensation (excluding current one)
    const existingCompensation = allParams.find(p =>
      p.name === title && p.name !== param.name
    );
    if (existingCompensation) {
      alert(`A compensation for ${compensation.parameter} already exists.`);
      return;
    }

    setTabs(prevTabs => {
      const updatedTabs = [...prevTabs];
      updatedTabs[currentTab].groups[groupIdx].params[paramIdx] = {
        ...param,
        name: title,
        value: {
          parameter: compensation.parameter,
          adjustedBy: compensation.adjustedBy,
          ratios: compensation.ratios,
          compensations: compensation.compensations
        }
      };
      return updatedTabs;
    });
    setShowDialog(false);
  };

  const deleteCompensation = () => {
    setTabs(prevTabs => {
      const updatedTabs = [...prevTabs];
      updatedTabs[currentTab].groups[groupIdx].params.splice(paramIdx, 1);
      return updatedTabs;
    });
    setShowDeleteConfirm(false);
  };

  const addRelatedCompensation = () => {
    setCompensation({
      ...compensation,
      compensations: [...compensation.compensations, { param: '', ratios: [] }]
    });
  };

  const updateRelated = (index, field, value) => {
    const updated = [...compensation.compensations];
    if (field === 'ratios') {
      updated[index][field] = value;
    } else {
      updated[index][field] = value;
    }
    setCompensation({ ...compensation, compensations: updated });
  };

  const removeRelated = (index) => {
    const updated = compensation.compensations.filter((_, i) => i !== index);
    setCompensation({ ...compensation, compensations: updated });
  };

  const getExample = (param, adjustedBy, ratio) => {
    if (!param || !adjustedBy || !ratio) return '';
    const paramUnit = allParams.find(p => p.name === param)?.unit || '';
    const adjustedUnit = allParams.find(p => p.name === adjustedBy)?.unit || '';
    return (
      <span>
        <strong>{ratio} {adjustedUnit}</strong> of <strong>{adjustedBy}</strong> change to achieve <strong>1 {paramUnit}</strong> of <strong>{param}</strong> change
      </span>
    );
  };

  const getRelatedExample = (param, relatedParam, ratio) => {
    if (!param || !relatedParam || !ratio) return '';
    const paramUnit = allParams.find(p => p.name === param)?.unit || '';
    const relatedUnit = allParams.find(p => p.name === relatedParam)?.unit || '';
    return (
      <span>
        <strong>{relatedParam}</strong> is affected by <strong>{ratio} {relatedUnit}</strong> for every <strong>1 {paramUnit}</strong> change in <strong>{param}</strong>
      </span>
    );
  };

  return (
    <>
      {isNew ? (
        <button onClick={() => setShowDialog(true)} className="btn btn-save">New Compensation</button>
      ) : (
        <>
          <i
            className="material-icons param-edit-icon"
            onClick={() => setShowDialog(true)}
          >
            settings
          </i>
          <div className="param-action-placeholder">
            <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-group-action delete">−</button>
          </div>
        </>
      )}
      {showDialog && (
        <div className="overlay-card">
          <div className="card-container">
            <div className="page-title" style={{ paddingBottom: '20px' }}>
              <h1>{isNew ? 'New Compensation' : 'Edit Compensation'}</h1>
              <button onClick={() => setShowDialog(false)} className="close-button">✕</button>
            </div>
            <label>Parameter:</label>
            <div className="search-input-container">
              <input
                value={compensation.parameter}
                onChange={e => {
                  setCompensation({ ...compensation, parameter: e.target.value });
                  updateParameterSuggestions(e.target.value);
                }}
                onFocus={() => {
                  if (compensation.parameter.trim()) {
                    updateParameterSuggestions(compensation.parameter);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowParameterDropdown(false), 150);
                }}
                placeholder="Search parameter"
                className="event-editor-input"
              />
              {showParameterDropdown && parameterSuggestions.length > 0 && (
                <div className="search-dropdown">
                  {parameterSuggestions.map((param, index) => (
                    <div
                      key={param.name}
                      className="search-dropdown-item"
                      onClick={() => {
                        setCompensation({ ...compensation, parameter: param.name });
                        setShowParameterDropdown(false);
                        setParameterSuggestions([]);
                      }}
                    >
                      {param.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label>Adjusted By:</label>
            <div className="search-input-container">
              <input
                value={compensation.adjustedBy}
                onChange={e => {
                  setCompensation({ ...compensation, adjustedBy: e.target.value });
                  updateAdjustedBySuggestions(e.target.value);
                }}
                onFocus={() => {
                  if (compensation.adjustedBy.trim()) {
                    updateAdjustedBySuggestions(compensation.adjustedBy);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowAdjustedByDropdown(false), 150);
                }}
                placeholder="Search parameter"
                className="event-editor-input"
              />
              {showAdjustedByDropdown && adjustedBySuggestions.length > 0 && (
                <div className="search-dropdown">
                  {adjustedBySuggestions.map((param, index) => (
                    <div
                      key={param.name}
                      className="search-dropdown-item"
                      onClick={() => {
                        setCompensation({ ...compensation, adjustedBy: param.name });
                        setShowAdjustedByDropdown(false);
                        setAdjustedBySuggestions([]);
                      }}
                    >
                      {param.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <label>Ratio:</label>
            {(() => {
              const paramObj = allParams.find(p => p.name === compensation.parameter);
              const adjustedObj = allParams.find(p => p.name === compensation.adjustedBy);
              const numRatios = Math.max(
                paramObj?.isFourCornerGroup ? 4 : paramObj?.isFrontRearGroup ? 2 : paramObj?.leftRight ? 2 : 1,
                adjustedObj?.isFourCornerGroup ? 4 : adjustedObj?.isFrontRearGroup ? 2 : adjustedObj?.leftRight ? 2 : 1
              );
              if (numRatios === 1) {
                return (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    <input
                      value={compensation.ratios[0] || ''}
                      onChange={e => {
                        const newRatios = [...compensation.ratios];
                        newRatios[0] = e.target.value;
                        setCompensation({ ...compensation, ratios: newRatios });
                      }}
                      placeholder="4"
                      className="event-editor-input"
                      style={{ flex: 1 }}
                      type="number"
                    />
                    <span style={{ margin: '0 10px' }}>:</span>
                    <span>1</span>
                  </div>
                );
              } else {
                return (
                  <div className="param-card">
                    <div className="param-grid">
                      {Array.from({length: numRatios}, (_, i) => (
                        <div key={i} className="param-input-group">
                          <input
                            value={compensation.ratios[i] || ''}
                            onChange={e => {
                              const newRatios = [...compensation.ratios];
                              newRatios[i] = e.target.value;
                              setCompensation({ ...compensation, ratios: newRatios });
                            }}
                            placeholder="4"
                            className="event-editor-input"
                            type="number"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="param-unit-inline">:1</p>
                  </div>
                );
              }
            })()}
            <p className="compensation-example">Example: {getExample(compensation.parameter, compensation.adjustedBy, compensation.ratios[0])}</p>
            <label>Compensations:</label>
            {compensation.compensations.map((comp, index) => (
              <div key={index} style={{ marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="search-input-container" style={{ flex: 1 }}>
                    <input
                      value={comp.param}
                      onChange={e => {
                        updateRelated(index, 'param', e.target.value);
                        updateCompensationSuggestions(index, e.target.value);
                      }}
                      onFocus={() => {
                        if (comp.param.trim()) {
                          updateCompensationSuggestions(index, comp.param);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setCompensationDropdowns(prev => ({ ...prev, [index]: false })), 150);
                      }}
                      placeholder="Search parameter"
                      className="event-editor-input"
                    />
                    {compensationDropdowns[index] && compensationSuggestions[index]?.length > 0 && (
                      <div className="compensation-search-dropdown">
                        {compensationSuggestions[index].map((param, paramIndex) => (
                          <div
                            key={param.name}
                            className="search-dropdown-item"
                            onClick={() => {
                              updateRelated(index, 'param', param.name);
                              setCompensationDropdowns(prev => ({ ...prev, [index]: false }));
                              setCompensationSuggestions(prev => ({ ...prev, [index]: [] }));
                            }}
                          >
                            {param.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeRelated(index)} className="btn btn-group-action delete">−</button>
                </div>
                {(() => {
                  const compObj = allParams.find(p => p.name === comp.param);
                  const compNum = compObj?.isFourCornerGroup ? 4 : compObj?.isFrontRearGroup ? 2 : compObj?.leftRight ? 2 : 1;
                  if (compNum === 1) {
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
                        <input
                          value={comp.ratios[0] || ''}
                          onChange={e => {
                            const newRatios = [...comp.ratios];
                            newRatios[0] = e.target.value;
                            updateRelated(index, 'ratios', newRatios);
                          }}
                          placeholder="0.05"
                          className="event-editor-input"
                          style={{ flex: 1 }}
                          type="number"
                        />
                        <span style={{ margin: '0 10px' }}>:</span>
                        <span>1</span>
                      </div>
                    );
                  } else {
                    return (
                      <>
                        <div className="param-card">
                          <div className="param-grid">
                            {Array.from({length: compNum}, (_, i) => (
                              <div key={i} className="param-input-group">
                                <input
                                  value={comp.ratios[i] || ''}
                                  onChange={e => {
                                    const newRatios = [...comp.ratios];
                                    newRatios[i] = e.target.value;
                                    updateRelated(index, 'ratios', newRatios);
                                  }}
                                  placeholder="0.05"
                                  className="event-editor-input"
                                  type="number"
                                />
                              </div>
                            ))}
                          </div>
                          <p className="param-unit-inline">:1</p>
                        </div>
                      </>
                    );
                  }
                })()}
                <p className="compensation-example">Example: {getRelatedExample(compensation.parameter, comp.param, comp.ratios[0])}</p>
              </div>
            ))}
            <button onClick={addRelatedCompensation} className="btn btn-secondary">Add Related Compensation</button>
            <div className="event-editor-actions">
              <button onClick={isNew ? addCompensation : updateCompensation} className="btn btn-save">Save</button>
              <button onClick={() => setShowDialog(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to delete this compensation?"
          onConfirm={deleteCompensation}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </>
  );
}

export default CompensationEditor;